import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaCreditCard, FaLayerGroup, FaSpinner } from "react-icons/fa";
import SubscriptionPlanCard from "../components/SubscriptionPlanCard";
import { useTenant } from "../context/TenantContext";
import { useAppTheme } from "../context/AppThemeContext";
import { SUBSCRIPTION_PLANS } from "../data/subscriptionPlans";
import { getSubscriptionPlans } from "../api/payments";

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { activeTenant } = useTenant();
  const { isDarkMode } = useAppTheme();
  const [plans, setPlans] = useState(SUBSCRIPTION_PLANS);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("");

  useEffect(() => {
    let active = true;
    getSubscriptionPlans()
      .then((items) => {
        if (!active || !Array.isArray(items) || items.length === 0) return;
        const mapped = items.map((item) => {
          const code = String(
            typeof item === "string"
              ? item
              : item?.code || item?.value || item?.plan || item?.id || ""
          ).trim().toUpperCase();
          const localMatch = SUBSCRIPTION_PLANS.find((p) => p.value === code) || {};
          return {
            ...localMatch,
            value: code,
            name: item.name || item.title || localMatch.name || code,
            duration: item.duration || localMatch.duration || "",
            // Use exact backend-provided price; do not calculate or trust browser calculations
            price: Number(item.price ?? item.amount ?? localMatch.price ?? 0),
            period: item.period || localMatch.period || "",
            popular: item.popular ?? localMatch.popular ?? false,
            badge: item.badge || localMatch.badge,
            description: item.description || localMatch.description || "",
            features: Array.isArray(item.features) ? item.features : (localMatch.features || []),
          };
        });
        setPlans(mapped);
      })
      .catch(() => {
        // Fall back to default catalog on error
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const selectedPlanDetails = plans.find((plan) => plan.value === selectedPlan);

  const continueToPayment = () => {
    if (!selectedPlan) return;
    navigate(`/subscription-payment?plan=${encodeURIComponent(selectedPlan)}`);
  };

  const pageClass = isDarkMode
    ? "min-h-full bg-slate-950 py-8 text-slate-100"
    : "min-h-full bg-slate-50 py-8 text-slate-900";

  return (
    <div className={pageClass}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <section className="rounded-3xl bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 p-6 text-white shadow-2xl shadow-blue-900/20 sm:p-10">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-100">
            <FaLayerGroup aria-hidden="true" /> Upgrade your plan
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Choose the plan that fits your organization.</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-blue-100">
            Select a billing duration for {activeTenant?.name || "your organization"}. You can review the plan and final amount before opening secure checkout.
          </p>
        </section>

        <section className="mt-8">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-blue-600">
              <FaSpinner className="animate-spin text-lg" /> Loading available plans...
            </div>
          )}
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => (
              <SubscriptionPlanCard
                key={plan.value}
                plan={plan}
                selected={selectedPlan === plan.value}
                onSelect={setSelectedPlan}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>

          <div className={`mt-8 flex flex-col gap-4 border p-5 sm:flex-row sm:items-center sm:justify-between ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-blue-200 bg-blue-50"}`}>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Your selection</p>
              <p className={isDarkMode ? "mt-1 text-sm text-slate-300" : "mt-1 text-sm text-blue-800"}>
                {selectedPlanDetails
                  ? <><span className="font-bold">{selectedPlanDetails.name}</span> · {selectedPlanDetails.duration}</>
                  : "Select one of the plans above to continue."}
              </p>
            </div>
            <button
              type="button"
              onClick={continueToPayment}
              disabled={!selectedPlan}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-700 to-teal-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-700/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              <FaCreditCard aria-hidden="true" /> Continue to payment <FaArrowRight className="text-xs" />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
